// Surface 9, panel 4: the reading hour (U125; module 10 screens/2026-09-21-onboarding frame 5).
// D88 for the bar and the estimate, D125 for saying what happens next, D98 for the stop.
//
// WHAT THIS SCREEN USED TO BE, AND WHY IT CHANGED. An hour is the longest a person ever waits on
// Cobrain, and this screen spent it saying nothing: a percentage, a rotating quip, and a step
// list hardcoded to step one with three paragraphs of policy prose under it. U125's words for
// what it should be instead are a live account of Cobrain working, with the numbers moving while
// you watch -- because this hour is the first evidence anybody ever gets that the thing works.
//
// THE LEDGER IS PANEL 2's, FILLING. Same shape, same place, same read (onboard/held.ts), so what
// a person was shown BEFORE they agreed is literally the thing that now grows. Two surfaces each
// keeping their own list is how a heading goes missing from one of them for nine calls.
import { Logo } from './Logo.js';
import { IgmMark } from './IgmMark.js';
import { Ribbon } from './onboarding/Ribbon.js';
import type { OnboardingState } from './api.js';
import type { Found, Held, HeldLine } from './onboarding/api.js';
import './onboarding/onboarding.css';

const LINES = [
  'Cobrain is now loading up your life…',
  'Reading the back catalogue of you…',
  'Joining the dots. All of them.',
  'Your inbox, but with a brain attached…',
  'Finding out what you owe and who owes you…',
];

// One line each. The three paragraphs these replace said everything worth saying about privacy
// on the screen where nothing has been decided yet, and said it where a person waiting for a
// number will not read it; the promise that matters here is the one directly over the finds.
const STEPS = [
  { title: 'Reading your mailbox', body: 'Six months of sent mail, then the last two weeks of everything. Passwords and passport numbers are blanked before anything is stored, and your mailbox itself is not changed.' },
  { title: 'Sorting', body: 'What is business, what is personal, what belongs to the same conversation, and who may see what.' },
  { title: 'Your first triage', body: 'What you owe, what you are owed, and a next move for each.' },
];

const n = (v: number): string => v.toLocaleString('en-GB');

function remaining(etaAt: string | null): string | null {
  if (!etaAt) return null;
  const ms = Date.parse(etaAt) - Date.now();
  if (Number.isNaN(ms) || ms <= 0) return 'almost there';
  const minutes = Math.round(ms / 60000);
  if (minutes < 1) return 'under a minute';
  if (minutes < 60) return `about ${minutes} minute${minutes === 1 ? '' : 's'}`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return `about ${hours} hour${hours === 1 ? '' : 's'}${rest ? ` ${rest} min` : ''}`;
}

/** "09:14", the person's own clock. Never a date: this screen is one sitting. */
function clock(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

/** How long it has been running, in the words a person uses for a wait they are sitting through. */
function running(startedAt: string | null): string | null {
  if (!startedAt) return null;
  const ms = Date.now() - Date.parse(startedAt);
  if (Number.isNaN(ms) || ms < 0) return null;
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) return 'just started';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return `${hours} hr${hours === 1 ? '' : 's'} ${minutes % 60} min`;
}

/** "just now", "3 min" -- how long ago a find landed. Minutes only; this screen is one hour long. */
export function ago(at: string, now: number = Date.now()): string {
  const ms = now - Date.parse(at);
  if (Number.isNaN(ms) || ms < 90_000) return 'just now';
  const minutes = Math.floor(ms / 60000);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return `${hours} hr${hours === 1 ? '' : 's'}`;
}

/** The person's word for what kind of thing was found. Never the table's. */
const FOUND_WORD: Record<Found['kind'], string> = {
  company: 'Company',
  person: 'Person',
  meeting: 'Meeting',
  owed: 'Owed',
};

/**
 * Which step is happening. The mailbox finishing is the only transition this screen can see
 * honestly: sorting has no completion of its own to read here, and a step list that advances on
 * a timer would be the screen telling the person something Cobrain does not know.
 */
export function stepOfReading(state: OnboardingState): number {
  return state.completedAt ? 1 : 0;
}

export function Steps({ current }: { current: number }) {
  return (
    <section className="steps" aria-label="What is happening">
      <h2>What is happening</h2>
      <ol>
        {STEPS.map((s, i) => (
          <li key={s.title} className={i === current ? 'now' : i < current ? 'done' : ''}>
            <strong>{s.title}</strong>
            <span className="tag">{i === current ? 'Happening now' : i < current ? 'Done' : i === current + 1 ? 'Next' : 'Then'}</span>
            <p>{s.body}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

/**
 * The ledger, filling. Drawn from the same `Held` the screen before it drew, so the figures a
 * person agreed to are the figures that grow. A line with nothing back still says so in words
 * rather than standing a zero in for a silence (U137).
 */
function Ledger({ held }: { held: Held }) {
  return (
    <div className="ledger" aria-label="What Cobrain has read so far">
      {held.lines.map((l: HeldLine) => (
        <div key={l.kind} className={l.reading ? 'lg live' : 'lg'}>
          {l.count === null
            ? <span className="s">{l.note}</span>
            : <><span className="n">{n(l.count)}</span><span className="w">{l.what}</span>
              <span className="s">{l.from}{l.reading ? ', still arriving' : ''}</span></>}
        </div>
      ))}
    </div>
  );
}

export function LoadingScreen({ state, tick, waitingOn = null, held = null, found = null, ribbon = false }: {
  state: OnboardingState; tick: number; waitingOn?: string | null;
  held?: Held | null; found?: Found[] | null;
  /** Whether this is the flow's own panel 4 (with the step strip) or App.tsx's bare gate. */
  ribbon?: boolean;
}) {
  if (state.status === 'failed') {
    return (
      <main className={ribbon ? 'mc onb has-ribbon' : 'mc onb'}>
        {ribbon ? <Ribbon at="reading" /> : null}
        <Logo size={44} />
        <div className="load-parked" role="alert">
          <b>Loading has stopped</b>
          <p>{state.failureMessage ?? 'Something went wrong while loading. Your admin has been told.'}</p>
          <p>Nothing is lost. When it is fixed, loading picks up where it stopped, and this page moves on by itself.</p>
        </div>
      </main>
    );
  }
  const expected = state.expectedItems ?? 0;
  const pct = expected > 0 ? Math.min(100, Math.floor((state.doneItems / expected) * 100)) : 0;
  const eta = expected > 0 ? remaining(state.etaAt) : null;
  const line = LINES[Math.floor(tick / 6) % LINES.length];
  const began = clock(state.startedAt);
  const been = running(state.startedAt);
  return (
    <main className={ribbon ? 'mc onb has-ribbon' : 'mc onb'}>
      {ribbon ? <Ribbon at="reading" /> : null}
      <Logo size={44} spinning />
      <div className="load">
        <div className="load-bug">
          <p className="load-title"><IgmMark height={20} />Reading</p>
          <span className="load-pct">
            <span className="digit">{pct}</span>
            <span className="of">per cent read</span>
          </span>
        </div>
        <div className="bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label="How much of your mailbox has been read">
          <div className="fill" style={{ width: `${pct}%` }} />
        </div>
        {/* All three clocks, so a run that has stopped moving is obvious from the screen rather
            than from the bar failing to move while nobody is watching. */}
        <p className="load-clocks">
          {began ? <span>Started {began}</span> : null}
          {been ? <span>Running {been}</span> : null}
          <span>{eta ? `${eta} left` : expected === 0 ? 'Counting your mail before it starts' : 'Working out how much is left'}</span>
        </p>
        {/* The source still to answer, named. Without this line a person whose mailbox has
            finished and whose notetaker was never reachable waits in front of a full bar with
            nothing on the screen saying what is missing (onboarding/journey.ts waitingOn). */}
        {waitingOn ? <p className="load-left">Waiting on {waitingOn}. Your mailbox is read; this page moves on once {waitingOn} answers.</p> : null}

        {held ? <Ledger held={held} /> : null}

        {found && found.length > 0 ? (
          <section className="found" aria-label="What Cobrain has found">
            <h2>Found in the last few minutes</h2>
            <ol>
              {found.map((f) => (
                <li key={`${f.kind}-${f.title}-${f.at}`}>
                  <span className="k">{FOUND_WORD[f.kind]}</span>
                  <span className="t">{f.title}{f.note ? <span className="sub">, {f.note}</span> : null}</span>
                  <span className="a">{ago(f.at)}</span>
                </li>
              ))}
            </ol>
            {/* The strongest thing this screen says, and it is true by construction: everything
                above was read back as this person, under the rails, in their own request. */}
            <p className="vow-line">Only you can see this list. Shared mailboxes and automatic senders are left off it.</p>
          </section>
        ) : null}

        <p className="quip">{line}</p>
        <Steps current={stepOfReading(state)} />
        <p className="meta">Nothing to prepare. You can close this page, it carries on without you.</p>
      </div>
    </main>
  );
}
